/* ============================================================================
 * Motive PowerCare — Proactive Fleet Management landing page
 * ----------------------------------------------------------------------------
 * SCOPED SCRIPT. Loaded ONLY on templates/page.motive-powercare.json via a
 * conditional include in layout/theme.liquid. Vanilla JS, no jQuery dependency.
 *
 * Responsibilities:
 *   - mpc-subnav: smooth-scroll to in-page anchors + scrollspy active state +
 *     keep the active link visible in the mobile horizontal scroller.
 *   - Band textures: paint each textured band's one-off image as ONE seamless
 *     image across the several sections that band spans, plus a subtle parallax
 *     on The Challenge.
 *   - (later) mpc-fleet-dashboard count-up animation — stub included.
 *   - mpc-exit: exit-intent offer popup — leave-detection, focus trap, and
 *     copy-to-clipboard on the discount code.
 *   - Let's Talk Fleet: grow the HubSpot form's fixed-height iframe while its
 *     date picker is open, so the calendar is not clipped by the frame edge.
 * ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* iOS Safari fires `resize` every time the URL bar collapses or expands — and
     that happens WHILE you are scrolling. Anything wired straight to `resize`
     therefore re-runs mid-gesture: the header gets re-measured, the sub-nav's
     sticky `top` is rewritten, the bands repaint. The layout shifts under your
     thumb repeatedly during one flick, which is the shape of the reported
     wiggle.

     A URL-bar toggle changes the viewport HEIGHT only. A genuine resize —
     rotation, or a desktop window being dragged — changes the width. So only
     act on a width change. Each caller keeps its own last-width, because a
     shared one would let whichever handler ran first swallow the event for
     every other handler. */
  function onRealResize(fn) {
    var w = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === w) return;
      w = window.innerWidth;
      fn();
    }, { passive: true });
  }

  /* Resolve a subnav href (#anchor) to its on-page target element. Targets are
     marked with a `mpc-anchor-<name>` css_class on the section (JSON templates
     cap at 25 sections, so we avoid separate anchor sections). Also supports an
     element id, a legacy (PRC) Hidden Anchor <a name="...">, or a data attr. */
  function resolveTarget(hash) {
    if (!hash || hash.charAt(0) !== '#') return null;
    var key = hash.slice(1);
    return document.querySelector('.mpc-anchor-' + CSS.escape(key)) ||
           document.getElementById(key) ||
           document.querySelector('a[name="' + CSS.escape(key) + '"]') ||
           document.querySelector('[data-mpc-anchor="' + CSS.escape(key) + '"]');
  }

  /* Total offset to leave above a target when scrolling: sticky subnav height
     plus any sticky header, read from CSS custom properties with fallbacks. */
  function scrollOffset(subnav) {
    var styles = getComputedStyle(document.documentElement);
    var top = parseInt(styles.getPropertyValue('--mpc-subnav-top'), 10) || 0;
    var h = subnav ? subnav.offsetHeight : (parseInt(styles.getPropertyValue('--mpc-subnav-h'), 10) || 58);
    return top + h + 8;
  }

  function initSubnav() {
    var subnav = document.querySelector('.mpc-subnav');
    if (!subnav) return;

    var links = Array.prototype.slice.call(subnav.querySelectorAll('.mpc-subnav__link'));
    if (!links.length) return;

    // Build [{ link, target }] for links that resolve to a real on-page anchor.
    var map = links.map(function (link) {
      return { link: link, target: resolveTarget(link.getAttribute('href') || '') };
    }).filter(function (e) { return e.target; });

    // Smooth scroll on click, accounting for the sticky offset.
    map.forEach(function (entry) {
      entry.link.addEventListener('click', function (e) {
        e.preventDefault();
        var y = entry.target.getBoundingClientRect().top + window.pageYOffset - scrollOffset(subnav);
        window.scrollTo({ top: y, behavior: 'smooth' });
        history.replaceState(null, '', entry.link.getAttribute('href'));
      });
    });

    // Scrollspy: mark the link whose section is currently in view.
    var list = subnav.querySelector('.mpc-subnav__list');
    var active = null;

    function setActive(activeLink) {
      // Only touch the DOM when the active section actually CHANGES. This used
      // to run on every scroll frame, which is what made the page unscrollable
      // on a phone: see the scroll note below.
      if (activeLink === active) return;
      active = activeLink;

      links.forEach(function (l) { l.classList.remove('is-active'); });
      if (!activeLink) return;
      activeLink.classList.add('is-active');

      /* Keep the active link visible inside the mobile horizontal scroller by
         writing the LIST's own scrollLeft.

         NOT scrollIntoView: that scrolls every scrollable ancestor it needs to,
         the document included. The link lives in a sticky bar, so the browser
         would regularly decide the bar was not quite in view and nudge the
         WINDOW — which fired `scroll`, which re-ran the scrollspy, which called
         scrollIntoView again. That feedback loop is the reported "it fights
         you": on iOS each nudge also cancels the momentum fling, so the page
         locks mid-flick and then wiggles as the loop settles.

         scrollLeft on the list can only ever move the list. (The old overflow
         test compared the list's scrollWidth against the SUBNAV's clientWidth,
         two different boxes, so it could be wrong in both directions.) */
      if (!list || list.scrollWidth <= list.clientWidth) return;
      var to = activeLink.offsetLeft - (list.clientWidth - activeLink.offsetWidth) / 2;
      var max = list.scrollWidth - list.clientWidth;
      list.scrollLeft = to < 0 ? 0 : (to > max ? max : to);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var probe = scrollOffset(subnav) + 4;
        var current = null;
        for (var i = 0; i < map.length; i++) {
          var top = map[i].target.getBoundingClientRect().top;
          if (top - probe <= 0) current = map[i].link;
        }
        // Nothing is active until a section has actually reached the probe
        // line. The old "default to the first link below 200px of scroll"
        // fallback lit Overview on load, dropped it the moment you passed
        // 200px — before Smart Fleet had reached the probe — then lit it again,
        // which read as a flicker. Overview now highlights when you arrive at
        // Overview, and not before.
        setActive(current);
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // A real resize changes the strip's width, so the centring has to be redone
    // even if the same section is still active — clear the memo to force it
    // through. (Not on an iOS URL-bar toggle: that would re-centre the strip
    // mid-scroll, every time the bar moved.)
    onRealResize(function () { active = null; onScroll(); });
    onScroll();
  }

  /* ---- Band textures: one image across several sections ------------------
     Three of the colour bands carry a one-off texture image (see the "Section
     textures" note in motive-powercare.css), and each band spans TWO OR MORE
     consecutive sections. Worse, the vertical padding lives on the outer
     .shopify-section wrapper while the colour lives on the inner <section>, so
     a two-section band has FOUR elements painting the background.

     Neither texture is a tile — ibplines.png is a single drawing and
     papertexture.jpg has a vignette — so every element has to show its own
     slice of ONE virtual image: identical background-size, and a
     background-position offset by exactly how far that element sits below and
     right of the band's top-left corner. Leave them all at their own origin and
     the artwork restarts at every section boundary, which is the tiled look
     this replaced.

     The Challenge additionally gets a deliberately SUBTLE parallax: the shared
     origin slides by TRAVEL x the band height over the band's whole transit of
     the viewport — a few dozen pixels, enough to feel alive and not enough to
     read as sliding. It is background-position only, so nothing is composited,
     transformed or reflowed and the subnav's scrollspy is untouched, and it can
     never bleed past the band because an element only ever paints inside
     itself. Under prefers-reduced-motion: reduce the bands are still painted
     (seamlessness is not decoration) but nothing moves on scroll.

     Full Visibility is intentionally absent: it is flat black with no image. */
  function initBandTextures() {
    var BANDS = [
      // fit 'width': scale the art by the band's width and preserve its aspect,
      // so the streaks keep their drawn angle and thickness. The band is taller
      // than the art, and the uncovered part is plain black — by design.
      // `bandAnchor` is where the art's TOP edge sits in the band, and the art's
      // top edge is where its topmost streak begins — so this is the control
      // that decides the streaks enter the left edge below the copy and sweep
      // down behind the cards, as they do in the mockup, rather than cutting
      // across the heading. `scale` slightly overhangs the band width for the
      // same reason: the mockup crops the drawing rather than fitting it.
      { sels: ['.mpc-challenge-head', '.mpc-challenge'],
        fit: 'width', ratio: 1054 / 3666, scale: 1.12, bandAnchor: 0.38,
        minShare: 0.42, parallax: 0.08 },
      // fit 'cover': the grain has to fill the band edge to edge, and cropping
      // it is free — it is noise.
      { sels: ['.mpc-approach-head', '.mpc-approach'],
        fit: 'cover', ratio: 1234 / 2000, natural: 2000,
        tileRatio: 1, tilePaint: 600, alwaysTile: true },
      // `gradients` counts the CSS background layers UNDER the texture. They get
      // the band's own box rather than each element's, so the orange gradient
      // runs continuously down the whole band instead of restarting — which is
      // visible as a tonal step at each section boundary.
      { sels: ['.mpc-services-head', '.mpc-services', '.mpc-services-cta'],
        fit: 'cover', ratio: 1234 / 2000, natural: 2000, gradients: 1,
        tileRatio: 1, tilePaint: 600, alwaysTile: true },
      // Single-section band, but its wrapper's padding is still a second
      // element; without this the wrapper and the section size the grain to
      // different boxes and it steps at the padding edge.
      { sels: ['.mpc-enroll-cta'],
        fit: 'cover', ratio: 1234 / 2000, natural: 2000, gradients: 1,
        tileRatio: 1, tilePaint: 600, alwaysTile: true }
    ];

    var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    var still = !!(mq && mq.matches);

    /* Phones do not get the scroll-driven parallax at all. Every frame of it
       costs a forced layout (getBoundingClientRect on ~9 band elements) and
       then a repaint of full-bleed textured bands — which is the difference
       between scroll that glides and scroll that drags. The payoff is a few
       dozen pixels of background drift. Not worth it on a handset; the bands
       are still painted seamlessly, which was never the decorative part. */
    var smallMq = window.matchMedia &&
                  window.matchMedia('(hover: none), (max-width: 860px)');
    function moving() { return !still && !(smallMq && smallMq.matches); }

    var bands = BANDS.map(function (spec) {
      var els = [];
      spec.sels.forEach(function (sel) {
        var inner = document.querySelector(sel);
        if (!inner) return;
        var wrapper = inner.closest && inner.closest('.shopify-section');
        if (wrapper && wrapper !== inner) els.push(wrapper);
        els.push(inner);
      });
      if (!els.length) return null;
      spec.els = els;
      return spec;
    }).filter(Boolean);
    if (!bands.length) return;

    function paint(movingOnly) {
      var vh = window.innerHeight || document.documentElement.clientHeight;

      // A scroll only ever changes the parallax band's origin; the other three
      // are pinned to their own geometry and cannot have moved relative to it.
      // Measuring and rewriting all four per frame was work with no output.
      var live = movingOnly
        ? bands.filter(function (b) { return b.parallax; })
        : bands;
      if (!live.length) return;

      // Read every rect first, then write every style — one layout pass.
      live.forEach(function (band) {
        band.rects = band.els.map(function (el) { return el.getBoundingClientRect(); });
        var top = Infinity, bottom = -Infinity, left = Infinity, width = 0;
        band.rects.forEach(function (r) {
          if (r.top < top) { top = r.top; }
          if (r.bottom > bottom) { bottom = r.bottom; }
          if (r.left < left) { left = r.left; }
          if (r.width > width) { width = r.width; }
        });
        band.top = top; band.height = bottom - top; band.left = left; band.width = width;
      });

      live.forEach(function (band) {
        if (!band.width || !band.height) return;

        var artW, artH, base, tile = false;
        if (band.fit === 'cover') {
          var narrow = band.width <= 720;

          // Does the original have to be magnified past its own pixel width to
          // cover this band? If so it cannot be used as it is, and the answer
          // is to repeat it rather than to stretch it.
          var needed = Math.max(band.width, band.height / band.ratio);
          // `alwaysTile` pins the orange bands to the tiled treatment. Deciding
          // it from band height alone made the look flip as section padding was
          // tuned: trimming a little space dropped the band under the threshold
          // and it silently swapped back to the vignetted original at ~1:1,
          // which reads as coarse. Their height is never in doubt, so the
          // decision should not be either.
          tile = !!(band.alwaysTile || (band.natural && needed > band.natural));

          if (tile) {
            // Repeat the SEAMLESS file, never the original. papertexture.jpg
            // carries a vignette, so tiling it repeats those dark corners and
            // the joins are plainly visible. This is why Program Services
            // seamed at desktop width — its band is tall enough to force
            // tiling — while the far shorter Our Approach band never tiles and
            // still shows the original, vignette and all.
            var ratio = band.tileRatio || band.ratio;

            // Painted at an explicit size rather than a fraction of the file's
            // own dimensions. The tile is deliberately drawn smaller than it is
            // stored — the extra pixels are averaged down, which is what keeps
            // the grain fine and free of the source JPEG's artefacts — and
            // pinning the number here means re-exporting the art at a different
            // resolution cannot silently change how coarse the page looks.
            var scale = band.tilePaint;

            // Then shrink it deliberately. Drawn at 1:1 the grain reads soft:
            // the source is a heavily compressed JPEG, so its artefacts are
            // about a CSS pixel each, and a 2x/3x screen draws every one of
            // them 2-3 device pixels wide. Painting the tile smaller packs more
            // grains into the same area and pushes the artefacts below the size
            // the eye resolves, which reads as sharper. Shrinking noise costs
            // nothing — there is no composition to lose — whereas stretching it
            // is what made it mush in the first place.
            if (narrow) scale = Math.round(scale * 0.9);

            artW = scale;
            artH = scale * ratio;
          } else {
            artW = needed;
            artH = needed * band.ratio;
          }
          base = (band.height - artH) / 2;
        } else {
          artW = band.width * band.scale;
          artH = artW * band.ratio;
          // On narrow viewports the band grows very tall as the cards stack,
          // which would leave the artwork a thin ribbon in a sea of black. Let
          // it scale up, but never enough to look like a different drawing.
          var grow = (band.height * band.minShare) / artH;
          if (grow > 1) { grow = grow > 2 ? 2 : grow; artW *= grow; artH *= grow; }
          base = band.bandAnchor * band.height;
        }

        if (band.parallax && moving()) {
          // t: 0 as the band's top edge enters at the bottom of the viewport,
          // 1 once it has fully passed the top. Clamped, so an off-screen band
          // holds at its end position instead of drifting away.
          var t = (vh - band.top) / (vh + band.height);
          t = t < 0 ? 0 : (t > 1 ? 1 : t);
          base += (t - 0.5) * band.height * band.parallax;
        }

        band.els.forEach(function (el, i) {
          var r = band.rects[i];
          // Offsets of this element from the band's top-left. Every layer is
          // placed against the BAND, so the whole stack lines up across the
          // sections instead of restarting in each one.
          var dx = band.left - r.left, dy = band.top - r.top;
          var size = [artW + 'px ' + artH + 'px'];
          var pos  = [(dx + (band.width - artW) / 2) + 'px ' + (base + dy) + 'px'];
          for (var g = 0; g < (band.gradients || 0); g++) {
            size.push(band.width + 'px ' + band.height + 'px');
            pos.push(dx + 'px ' + dy + 'px');
          }
          // Only the clamped texture layer tiles; the gradients under it stay
          // single-shot so they keep spanning the whole band.
          var repeat = [tile ? 'repeat' : 'no-repeat'];
          for (var q = 0; q < (band.gradients || 0); q++) repeat.push('no-repeat');

          // Tells the stylesheet which file to paint. Only bands that actually
          // repeat get the seamless tile; the rest keep the original artwork
          // with its vignette, which is wanted when it is shown whole.
          el.classList.toggle('mpc-is-tiled', tile);

          var s = el.style;
          s.backgroundRepeat = repeat.join(', ');
          s.backgroundSize = size.join(', ');
          s.backgroundPosition = pos.join(', ');
        });
      });
    }

    var ticking = false;
    function schedule(movingOnly) {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { paint(movingOnly); ticking = false; });
    }

    // Resize/load still have to repaint under reduced motion (and on phones) —
    // the bands have to stay seamless — but scrolling has nothing to do there.
    var moves = bands.some(function (b) { return !!b.parallax; });
    function onScroll() { if (moves && moving()) schedule(true); }

    // Wrapped, not passed straight through: these fire with an Event / an array
    // of ResizeObserverEntries, and either one arriving as `movingOnly` would
    // read as truthy and quietly reduce a full repaint to the parallax band.
    function repaint() { schedule(false); }

    paint();
    onRealResize(repaint);
    window.addEventListener('load', repaint);
    window.addEventListener('scroll', onScroll, { passive: true });
    // Sections above a band can change height after paint (video posters, web
    // fonts, the sticky subnav collapsing), which moves the band without a
    // scroll or resize event.
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(repaint);
      bands.forEach(function (b) { b.els.forEach(function (el) { ro.observe(el); }); });
      ro.observe(document.body);
    }
    if (mq && mq.addEventListener) {
      mq.addEventListener('change', function (e) { still = e.matches; paint(); });
    }
  }

  /* Count-up animation for the (future) Fleet Health Dashboard. Opt-in via
     [data-mpc-countup] on a number element; harmless no-op until the dashboard
     is built. */
  function initCountUps() {
    var els = document.querySelectorAll('[data-mpc-countup]');
    if (!els.length || !('IntersectionObserver' in window)) return;

    function run(el) {
      var target = parseFloat(el.getAttribute('data-mpc-countup')) || 0;
      var dur = 1200, start = null;
      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        el.textContent = Math.round(target * (0.5 - Math.cos(Math.PI * p) / 2)).toLocaleString();
        if (p < 1) window.requestAnimationFrame(step);
      }
      window.requestAnimationFrame(step);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { run(en.target); io.unobserve(en.target); }
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* --------------------------------------------------------------------
   * Sub-nav sticky offset.
   * The theme header is `position: static` and scrolls away, so the sticky
   * sub-nav was pinning to the very top of the viewport. The header is made
   * sticky for this template in motive-powercare.css; this measures its real
   * height and parks the sub-nav directly beneath it via --mpc-subnav-top,
   * which the sticky rule and the scrollspy scroll offset both read.
   * Measured rather than hard-coded because the header's height changes with
   * breakpoint (and with the announcement bar, if it is ever switched on).
   * ------------------------------------------------------------------ */
  function initHeaderOffset() {
    var header = document.querySelector('#shopify-section-header-classic') ||
                 document.querySelector('header.header') ||
                 document.querySelector('header');
    if (!header) return;

    function apply() {
      // Only offset while the header is actually sticking to the top; if the
      // stylesheet stops making it sticky, fall back to a flush sub-nav.
      var sticky = getComputedStyle(header).position === 'sticky' ||
                   getComputedStyle(header).position === 'fixed';
      var h = sticky ? Math.round(header.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--mpc-subnav-top', h + 'px');
    }

    apply();
    // Width-changes only: on iOS the header's height does not change when the
    // URL bar slides, but rewriting --mpc-subnav-top mid-scroll moves the
    // sticky sub-nav anyway. The ResizeObserver still catches a header that
    // genuinely changes height (announcement bar, wrapped nav).
    onRealResize(apply);
    if (window.ResizeObserver) new ResizeObserver(apply).observe(header);
  }

  /* --------------------------------------------------------------------
   * "Our Approach" staggered reveal.
   * Steps and the arrows between them fade up in document order, so the eye is
   * led 1 -> arrow -> 2 -> arrow -> 3 …  Each element carries its position as
   * --mpc-step-index and the stylesheet turns that into a transition-delay.
   * The `is-reveal-ready` class (which hides them) is added by JS, never in
   * markup — if this script fails the content stays visible rather than blank.
   * ------------------------------------------------------------------ */
  function initStepFlowReveal() {
    /* Same staggered treatment on the Challenge cards. Selector pairs are
       [container, children] so one routine covers both sections. */
    var groups = [
      ['.mpc-stepflow', '.mpc-stepflow__step, .mpc-stepflow__arrow', false],
      ['.mpc-challenge', '.icon-with-text-column__column', false],
      ['.mpc-services', '.icon-with-text-column__column', false],
      ['.mpc-visibility', '.icon-with-text-column__column', false],
      // Whole band together: video 1, video 2, then the feature card — the
      // selector returns them in document order, so the card lands last.
      ['.mpc-pcp__inner', '.mpc-pcp__video, .mpc-pcp__card', false]
    ];
    groups.forEach(function (g) { revealGroup(g[0], g[1], g[2]); });
  }

  function revealGroup(containerSel, itemSel, byRow) {
    var flows = document.querySelectorAll(containerSel);
    if (!flows.length) return;

    Array.prototype.forEach.call(flows, function (flow) {
      var items = flow.querySelectorAll(itemSel);
      if (!items.length) return;

      if (byRow) {
        // Group by rounded offsetTop so every card in a row shares one index,
        // and the rows fade in one after another.
        var rows = [];
        Array.prototype.forEach.call(items, function (el) {
          var top = Math.round(el.offsetTop);
          var row = rows.indexOf(top);
          if (row === -1) { rows.push(top); row = rows.length - 1; }
          el.style.setProperty('--mpc-step-index', row);
        });
      } else {
        Array.prototype.forEach.call(items, function (el, i) {
          el.style.setProperty('--mpc-step-index', i);
        });
      }
      flow.classList.add('is-reveal-ready');

      if (!('IntersectionObserver' in window)) {
        flow.classList.add('is-revealed');
        return;
      }

      /* On a phone these containers are enormous — the Program Services grid is
         over 1600px tall against an ~844px viewport — so watching the CONTAINER
         means the whole group fires once a quarter of it is visible, and the
         cards near the bottom finish animating hundreds of pixels before anyone
         scrolls to them. Below 720px each item is therefore observed on its own
         and reveals as it arrives. The wide layout keeps the container-level
         stagger, which is the point of it: there the cards sit side by side and
         are all on screen together. */
      if (window.matchMedia('(max-width: 720px)').matches) {
        flow.classList.add('is-item-reveal');
        var ioItems = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) return;
            en.target.classList.add('is-revealed');
            ioItems.unobserve(en.target);
          });
        }, { threshold: 0.2, rootMargin: '0px 0px -12% 0px' });
        Array.prototype.forEach.call(items, function (el) { ioItems.observe(el); });
        return;
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          en.target.classList.add('is-revealed');
          io.unobserve(en.target);

          /* The staggered reveal delay lives on the same `transition` the hover
             lift uses, so once the sequence had run every card kept its delay —
             the fifth Challenge card waited 560ms before it moved under the
             cursor. `is-settled` lands after the whole sequence has finished
             and hands the transition back to hover at a hover-appropriate
             speed. */
          window.setTimeout(function () {
            en.target.classList.add('is-settled');
          }, items.length * 140 + 600);
        });
      }, { threshold: 0.25 });
      io.observe(flow);
    });
  }

  /* --------------------------------------------------------------------
   * PC Platinum video facades.
   * Each video shows a poster + play button; the YouTube/Vimeo iframe is only
   * injected on click. Two autoplaying embeds in one band would be wasteful,
   * and this also keeps the third-party player (and its cookies) off the page
   * for visitors who never press play. Plays INLINE — no modal.
   * ------------------------------------------------------------------ */
  function initPlatinumVideos() {
    var buttons = document.querySelectorAll('[data-mpc-video]');
    if (!buttons.length) return;

    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        var src = btn.getAttribute('data-embed');
        if (!src) return;

        var frame = document.createElement('iframe');
        frame.setAttribute('src', src);
        frame.setAttribute('title', btn.getAttribute('aria-label') || 'Video');
        frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture');
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('loading', 'lazy');

        btn.parentNode.replaceChild(frame, btn);
        // Move focus into the player so keyboard users are not dropped at the
        // top of the document when the button they activated disappears.
        frame.setAttribute('tabindex', '-1');
        frame.focus({ preventScroll: true });
      });
    });
  }

  /* ---- Exit-intent popup -------------------------------------------------
   * Shows the offer once when the visitor looks like they are leaving.
   *
   * "Looks like leaving" is two different gestures depending on the device:
   * on a pointer device it is the cursor crossing the TOP edge of the viewport
   * (toward the tabs / address bar); on touch there is no cursor to track at
   * all, so the tell is a fast upward flick after having scrolled a fair way
   * down — the scroll-back-to-top that precedes a back-button press.
   *
   * Everything is gated behind a minimum dwell time so it cannot fire at
   * someone who has only just landed, and behind a localStorage stamp so a
   * visitor who has already seen it is left alone.
   * -------------------------------------------------------------------- */
  function initExitPopup() {
    var root = document.querySelector('[data-mpc-exit]');
    if (!root) return;

    var panel = root.querySelector('.mpc-exit__panel');
    if (!panel) return;

    var days     = parseInt(root.getAttribute('data-mpc-exit-days'), 10);
    var minSecs  = parseInt(root.getAttribute('data-mpc-exit-delay'), 10) || 0;
    var storeKey = 'mpc-exit-seen';
    var landedAt = Date.now();
    var shown    = false;
    var lastFocus = null;

    if (isNaN(days)) days = 30;

    // In the Theme Editor the popup must be reachable on demand — otherwise it
    // cannot be styled — so the seen-stamp and the dwell gate are bypassed
    // there and selecting the section opens it.
    var designMode = !!(window.Shopify && window.Shopify.designMode);

    function seen() {
      if (designMode) return false;
      try {
        // days === 0 means "once per session", which is sessionStorage's job.
        var store = days === 0 ? window.sessionStorage : window.localStorage;
        var stamp = store.getItem(storeKey);
        if (!stamp) return false;
        if (days === 0) return true;
        return (Date.now() - parseInt(stamp, 10)) < days * 86400000;
      } catch (e) {
        // Private mode / storage disabled: fail toward NOT nagging.
        return true;
      }
    }

    function markSeen() {
      try {
        (days === 0 ? window.sessionStorage : window.localStorage)
          .setItem(storeKey, String(Date.now()));
      } catch (e) { /* nothing to do */ }
    }

    // Keep Tab inside the dialog while it is open.
    var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function onKeydown(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last  = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    /* `stamp` is false when the popup was opened deliberately rather than by
       the visitor's own behaviour — the ?mpc-exit=1 testing hatch and the Theme
       Editor. Without that distinction, anyone following the test link would be
       recorded as having seen it and could never trigger it naturally
       afterwards, which is exactly what someone reviewing the page wants to
       try. */
    function open(stamp) {
      if (shown || root.classList.contains('is-open')) return;
      shown = true;
      if (stamp !== false) markSeen();

      lastFocus = document.activeElement;
      root.hidden = false;
      // One frame between removing `hidden` and adding `is-open`, so the
      // transition has a start state to animate away from.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { root.classList.add('is-open'); });
      });

      document.addEventListener('keydown', onKeydown);

      var target = panel.querySelector('.mpc-exit__close') ||
                   panel.querySelector(FOCUSABLE);
      if (target) target.focus({ preventScroll: true });
    }

    function close() {
      if (root.hidden) return;
      root.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown);

      var done = function () { root.hidden = true; };
      // Wait out the fade unless motion is off, in which case hide immediately.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else window.setTimeout(done, 280);

      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-mpc-exit-dismiss]'),
      function (el) { el.addEventListener('click', close); }
    );

    /* An in-page CTA (#get-started) has to go through the same resolver the
       subnav uses — those anchors are `mpc-anchor-<name>` CLASSES on a section,
       not element ids, so the browser's native hash jump finds nothing. Close
       first, then scroll, so the overlay is not sitting over the destination. */
    var cta = root.querySelector('.mpc-exit__cta');
    if (cta) {
      cta.addEventListener('click', function (e) {
        var href = cta.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;      // off-site link: let it be

        var target = resolveTarget(href);
        if (!target) return;

        e.preventDefault();
        close();
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.pageYOffset
               - scrollOffset(document.querySelector('.mpc-subnav')),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto' : 'smooth'
        });
      });
    }

    /* Copy-to-clipboard on the code. */
    var codeBtn = root.querySelector('[data-mpc-exit-copy]');
    var copied  = root.querySelector('[data-mpc-exit-copied]');
    if (codeBtn) {
      codeBtn.addEventListener('click', function () {
        var code = codeBtn.getAttribute('data-mpc-exit-copy') || '';
        var announce = function (ok) {
          codeBtn.classList.toggle('is-copied', ok);
          if (copied) copied.textContent = ok ? 'Copied' : 'Press Ctrl+C to copy';
          if (ok) {
            window.setTimeout(function () {
              codeBtn.classList.remove('is-copied');
              if (copied) copied.textContent = '';
            }, 2600);
          }
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(
            function () { announce(true); },
            function () { announce(false); }
          );
        } else {
          announce(false);
        }
      });
    }

    if (designMode) {
      document.addEventListener('shopify:section:select', function (e) {
        if (root.closest('.shopify-section') === e.target) { shown = false; open(false); }
      });
      document.addEventListener('shopify:section:deselect', close);
      return;
    }

    /* Testing hatch: `?mpc-exit=1` on the URL opens it immediately, ignoring
       both the dwell gate and the already-seen stamp. Exit intent is otherwise
       fiddly to demonstrate — you have to wait out the delay, leave through the
       top edge, and clear localStorage between attempts. */
    if (window.location.search.indexOf('mpc-exit=1') !== -1) {
      open(false);
      return;
    }

    if (seen()) return;

    function dwelled() { return (Date.now() - landedAt) >= minSecs * 1000; }

    // Pointer devices: cursor leaving through the top edge.
    document.addEventListener('mouseout', function (e) {
      if (shown || !dwelled()) return;
      if (e.relatedTarget || e.clientY > 0) return;   // still inside the page
      open();
    });

    /* Touch: the scroll-back-to-top that precedes a back-button press.

       This was a velocity test — 1.6px/ms across one scroll event — and that is
       not a rare gesture on a phone. An ordinary momentum fling runs several
       times faster, so the popup was landing on people who had merely flicked
       up to re-read something, which is a large part of the reported "it fights
       you on the scroll": the overlay opens mid-scroll and takes the gesture.

       The shape of a retreat is geometric, not fast. All three have to hold:
       they got a long way down, they have come most of the way back up in one
       unbroken movement, and they have arrived within half a screen of the top —
       which is where the back button actually gets pressed. Any downward
       movement resets the climb, because that is someone reading again. */
    if (window.matchMedia('(hover: none)').matches) {
      var lastY   = window.pageYOffset;
      var deepest = lastY;
      var climb   = 0;

      window.addEventListener('scroll', function () {
        var y  = window.pageYOffset;
        var dy = lastY - y;                 // positive while moving UP
        if (y > deepest) deepest = y;
        climb = dy > 0 ? climb + dy : 0;
        lastY = y;

        if (shown || !dwelled()) return;
        var vh = window.innerHeight;
        if (deepest < vh * 2) return;       // never got properly into the page
        if (climb < deepest * 0.6) return;  // not one sustained retreat
        if (y > vh * 0.5) return;           // not back at the top yet
        open();
      }, { passive: true });
    }
  }

  /* ---- Let's Talk Fleet: the HubSpot date picker gets cut off --------------
     The HubSpot embed does not render the form inline. It writes it into its
     own iframe and sizes that iframe to the form's RESTING height (686px as
     built), with scrolling="no". That is fine until the "when would you like
     to schedule your no-charge evaluation" field opens its calendar: the panel
     is absolutely positioned under the input and runs to 801px, so the bottom
     115px — four of the five week rows, today's date among them — sit past the
     frame's edge with no scrollbar to reach them. Reported by Jay Becker
     2026-08-25: "it doesn't show the entire calendar".

     Growing the frame to fit the panel does NOT work: the embed script keeps
     its own height on the element and re-asserts it, so any height we set is
     stomped within the same frame or two. Measured against the live page —
     the frame snapped straight back to 686px and all four rows stayed hidden.

     So leave the height alone and move the panel instead. It is absolutely
     positioned and sits at its static position (directly under the input), so
     making the field wrapper the containing block and pinning the panel to
     `bottom: 100%` flips it to open UPWARD, into space the frame already has.
     On the form as it stands that puts the calendar at 315–538px inside a
     686px frame, with nothing clipped and no argument with HubSpot.

     Flip only when it actually helps: if the panel would overflow the bottom
     AND there is room for it above. A form with more fields ahead of the date
     — IBP can add them in HubSpot without telling us — could put the input
     high enough that flipping up would clip the panel against the top edge
     instead, which is the same bug wearing a hat.

     HubSpot writes the frame from this same origin, so we can reach in to
     inject the rule and toggle the class. */
  function initHubspotFormFrame() {
    var host = document.querySelector('.mpc-lt__form');
    if (!host || !window.MutationObserver) return;

    var FLIP = 'mpc-flip-up';

    /* Scoped to the flip class, so a panel that opens downward keeps HubSpot's
       own geometry untouched. */
    var CSS = '.hs-dateinput.' + FLIP + '{position:relative;}' +
              '.hs-dateinput.' + FLIP + ' .hs-datepicker{top:auto !important;bottom:100% !important;}';

    function watch(frame) {
      if (frame.getAttribute('data-mpc-watched')) return;

      var doc;
      /* Defensive: should HubSpot ever serve the form from its own origin this
         throws, and there is nothing further we can do from out here. */
      try { doc = frame.contentDocument; } catch (e) { return; }
      if (!doc || !doc.body || !doc.head) return;
      frame.setAttribute('data-mpc-watched', '1');

      var style = doc.createElement('style');
      style.textContent = CSS;
      doc.head.appendChild(style);

      var queued = false;

      function sync() {
        queued = false;

        var wraps = doc.querySelectorAll('.hs-dateinput');
        var frameH = frame.getBoundingClientRect().height;

        Array.prototype.forEach.call(wraps, function (wrap) {
          var panel = wrap.querySelector('.hs-datepicker');
          if (!panel || !wrap.classList.contains('hs-datepicker-open')) {
            wrap.classList.remove(FLIP);
            return;
          }

          /* Measure unflipped, so the decision is made against where the panel
             would naturally sit rather than against last pass's answer. */
          wrap.classList.remove(FLIP);
          var panelH = panel.getBoundingClientRect().height;
          var wrapBox = wrap.getBoundingClientRect();

          var overflowsBelow = wrapBox.bottom + panelH > frameH;
          var fitsAbove = wrapBox.top - panelH >= 0;
          if (overflowsBelow && fitsAbove) wrap.classList.add(FLIP);
        });

        /* Toggling FLIP is itself a class mutation in the document we are
           watching. Drop the records we just generated, or the observer wakes
           us on our own work and sync runs every frame for as long as the
           picker is open. */
        observer.takeRecords();
      }

      function schedule() {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(sync);
      }

      var observer = new MutationObserver(schedule);
      observer.observe(doc.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    function scan() {
      var frames = host.querySelectorAll('iframe.hs-form-iframe');
      Array.prototype.forEach.call(frames, function (frame) {
        if (!frame.getAttribute('data-mpc-seen')) {
          frame.setAttribute('data-mpc-seen', '1');
          /* The element exists before its document is written, so try again
             once it loads. watch() is idempotent. */
          frame.addEventListener('load', function () { watch(frame); });
        }
        watch(frame);
      });
      return frames.length > 0;
    }

    /* The embed script injects the frame well after DOMContentLoaded. */
    if (scan()) return;
    var obs = new MutationObserver(function () { if (scan()) obs.disconnect(); });
    obs.observe(host, { subtree: true, childList: true });
  }

  /* ---- In-page anchor links anywhere on the page ---------------------------
     The band CTAs point at #get-started, but the in-page anchors are
     `mpc-anchor-<name>` CLASSES on a section rather than element ids (JSON
     templates cap at 25 sections, so separate anchor sections were avoided).
     The browser's native hash jump therefore finds nothing and the buttons do
     nothing. One delegated handler routes any in-page link through the same
     resolver the sub-nav uses, so a link set in the Theme Editor to
     "#get-started" simply works — and a real off-site URL from IBP later still
     behaves normally, since those never start with "#".

     The sub-nav and the popup CTA are skipped: both already have handlers that
     do more than scroll. */
  /* Scroll so `target` sits just below the sticky chrome. */
  function scrollToTarget(target, smooth) {
    window.scrollTo({
      top: Math.max(0, target.getBoundingClientRect().top + window.pageYOffset
           - scrollOffset(document.querySelector('.mpc-subnav'))),
      behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'smooth' : 'auto'
    });
  }

  /* The in-page hash a link points at, or null if it does not target a section
     of THIS page. Two shapes matter:

       "#get-started"                        — a bare in-page anchor
       "/pages/motive-powercare#pc-platinum" — what the main menu actually
                                               contains

     The second is the one that bit us. Matching only on href^="#" missed it
     entirely, and when you are ALREADY on this page the browser treats that
     link as a hash change rather than a navigation: no reload, so the
     load-time landing never runs either, and the click silently does nothing.
     Reported once the menu went live, 2026-08-26. */
  function samePageHash(a) {
    var href = a.getAttribute('href');
    if (!href || href === '#') return null;
    if (href.charAt(0) === '#') return href;

    var url;
    try { url = new URL(href, window.location.href); } catch (e) { return null; }
    if (!url.hash) return null;
    if (url.origin !== window.location.origin) return null;

    var here = window.location.pathname.replace(/\/+$/, '');
    var there = url.pathname.replace(/\/+$/, '');
    return there === here ? url.hash : null;
  }

  function initAnchorLinks() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.closest('.mpc-subnav')) return;
      if (a.classList.contains('mpc-exit__cta')) return;

      var hash = samePageHash(a);
      if (!hash) return;

      var target = resolveTarget(hash);
      if (!target) return;

      e.preventDefault();
      scrollToTarget(target, true);
      history.replaceState(null, '', hash);
    });

    /* Safety net for hash changes that never reach the handler above: browser
       back/forward, and menu scripts that stop propagation on their own links. */
    window.addEventListener('hashchange', function () {
      var target = resolveTarget(window.location.hash);
      if (target) scrollToTarget(target, true);
    });
  }

  /* ---- Arriving with a hash already in the URL -----------------------------
     initAnchorLinks() handles CLICKS made on this page. A link from the main
     menu is a different thing: a fresh page load with the hash already set.
     The browser's native jump finds nothing, because these anchors are
     `mpc-anchor-<name>` CLASSES on a section rather than element ids — so a
     visitor who picks "PC Platinum" out of the menu lands at the top of the
     page instead of at the section the menu just promised them.

     Resolve the hash through the same resolver the sub-nav uses. Waiting for
     `load` is NOT enough on its own: the hero video, the textured bands and
     the images above the target keep settling afterwards, so an offset
     measured once aims at a layout that no longer exists. Measured on the
     live page, a single pass 120ms after `load` overshot by 1647px — the
     section had moved up as content above it resolved, and the visitor landed
     well past it.

     So land, then keep checking briefly and correct. Each pass re-measures
     from scratch; once two consecutive passes agree the page has settled and
     we stop. If the visitor scrolls in the meantime we back off immediately —
     their scroll outranks ours, and yanking the page back under someone who
     has started reading is worse than landing slightly off.

     Jump instantly rather than smooth-scrolling — this is a landing, not a
     gesture the visitor made, and animating it just means watching the whole
     page fly past on arrival. */
  function initHashLanding() {
    if (!window.location.hash) return;

    var target = resolveTarget(window.location.hash);
    if (!target) return;

    /* Don't guess how long the page takes to settle — watch until it does.
       A fixed schedule was tried and lost: the passes all ran while the
       document was still tall, agreed with each other, and finished before
       the layout resolved, leaving the page pinned at max scroll with the
       section 1647px above the viewport.

       So poll until the computed target position stops moving (STABLE_TICKS
       in a row) or DEADLINE_MS passes, whichever comes first. */
    var TICK_MS = 150;
    var DEADLINE_MS = 6000;
    var STABLE_TICKS = 3;

    /* Detect the visitor taking over by listening for INPUT, not by watching
       the scroll position. The obvious version — "if scrollY is no longer
       where I put it, they must have scrolled" — is wrong here, and wrong in
       a way that silently disables every correction: when the document
       shrinks as content above resolves, the browser re-clamps scrollY on its
       own, which is indistinguishable from a person scrolling by that test.
       That is exactly what happened on the live page — the first pass
       overshot to the bottom, the document then shrank, the re-clamp read as
       a user scroll, and the corrections never ran. */
    var userMoved = false;
    var INPUT = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    function noteUser() { userMoved = true; release(); }
    function release() {
      INPUT.forEach(function (evt) { window.removeEventListener(evt, noteUser); });
    }
    INPUT.forEach(function (evt) {
      window.addEventListener(evt, noteUser, { passive: true });
    });

    function wanted() {
      return Math.max(0, Math.round(
        target.getBoundingClientRect().top + window.pageYOffset
        - scrollOffset(document.querySelector('.mpc-subnav'))
      ));
    }

    function start() {
      var began = Date.now();
      var lastWanted = null;
      var stable = 0;

      (function tick() {
        if (userMoved) return;

        var top = wanted();

        /* Stability is about where the SECTION has come to rest, not about
           where the window happens to be sitting — the window can be right
           while the page is still moving underneath it. */
        if (lastWanted !== null && Math.abs(top - lastWanted) <= 2) stable++;
        else stable = 0;
        lastWanted = top;

        if (Math.abs(window.pageYOffset - top) > 2) {
          window.scrollTo({ top: top, behavior: 'auto' });
        }

        if (stable >= STABLE_TICKS || Date.now() - began > DEADLINE_MS) {
          release();
          return;
        }
        window.setTimeout(tick, TICK_MS);
      })();
    }

    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start);
  }

  ready(function () {
    initHeaderOffset();
    initStepFlowReveal();
    initPlatinumVideos();
    initSubnav();
    initBandTextures();
    initCountUps();
    initExitPopup();
    initHubspotFormFrame();
    initAnchorLinks();
    initHashLanding();
  });
})();
